<template>
  <div class="chart-filters">
    <MatchMedia
      v-if="filters"
      v-slot="{ matches: isDesktop }"
      class="chart-filters__date"
      device="desktop"
    >
      <div v-if="isDesktop" data-testid="date-filter">
        <FilterOption
          v-for="(filter, index) in filters"
          :key="index"
          class="chart-filters__date__filter"
          :checked="selectedDateFilter"
          :label="filter.label"
          :value="filter.value"
          :disabled="filter.disabled"
          @change="onSelectFilter"
        />
      </div>
      <div v-else>
        <Select
          v-if="filters.length > 0"
          :on-select="option => onSelectFilter(option)"
          :default-value="getSelectedDateFilter"
          :options="filters"
          data-testid="date-select"
        />
      </div>
      <SpinnerLoader
        v-if="isLoading"
        class="chart-filters__loader"
        :size="16"
        :color="NebraskaColors.elementPlaceholder"
        data-testid="chart-filters-loader"
      />
    </MatchMedia>
    <div class="chart-filters__item">
      <Select
        v-if="benchmarkSelectOptions"
        :on-select="option => onSelectBenchmark(option)"
        :default-value="checkSelectedFilter(benchmarkSelectOptions)"
        :options="benchmarkSelectOptions"
        data-testid="benchmark-select"
        class="chart-filters__select"
      />
      <Select
        v-if="false"
        :on-select="option => onSelectChart(option)"
        :default-value="checkSelectedFilter(chartSelectOptions)"
        :options="chartSelectOptions"
        data-testid="chart-type-select"
        class="chart-filters__select"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Emit } from 'vue-property-decorator';

import {
  Select,
  FilterOption,
  SpinnerLoader
} from '@warrenbrasil/nebraska-web';
import { MatchMedia } from '@/modules/common/components/MatchMedia';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { IOption } from './types';

@Component({
  name: 'ChartFilters',
  components: { Select, FilterOption, MatchMedia, SpinnerLoader }
})
export default class ChartFilters extends Vue {
  @Prop({ type: Array, required: false })
  readonly filters?: IOption[];

  @Prop({ type: Array, required: false })
  readonly benchmarkSelectOptions?: IOption[];

  @Prop({ type: Array, required: false })
  readonly chartSelectOptions?: IOption[];

  @Prop({ type: Boolean, default: false })
  readonly isLoading!: boolean;

  readonly NebraskaColors = NebraskaColors;

  public selectedDateFilter: string | IOption = '';

  private created() {
    if (this.filters) {
      this.selectedDateFilter =
        this.checkSelectedFilter(this.filters)?.value || '';
    }
  }

  private get getSelectedDateFilter() {
    return (
      this.filters &&
      this.filters.find(
        (filter: IOption) => filter.value === this.selectedDateFilter
      )
    );
  }

  public checkSelectedFilter(filterOptions: IOption[]): IOption {
    const activeValue = filterOptions.find(
      (value: IOption) => value.active === true
    );
    return activeValue || filterOptions[0];
  }

  @Emit('tracking')
  private tracking(action: string, label: string) {
    return { action, label };
  }

  @Emit('on-select-filter')
  public onSelectFilter(filter: string | IOption) {
    this.selectedDateFilter = (filter as IOption).value || (filter as string);
    this.tracking('select-filter', this.selectedDateFilter);
    return this.selectedDateFilter;
  }

  @Emit('on-select-benchmark')
  public onSelectBenchmark({ value }: IOption): string {
    return value;
  }

  @Emit('on-select-chart')
  public onSelectChart({ value }: IOption): string {
    this.tracking('select-chart-type', value);
    return value;
  }
}
</script>

<style lang="less" scoped>
.chart-filters {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  min-height: 40px;

  &__date {
    display: flex;
    align-items: center;

    &__filter {
      margin-right: @size-spacing-x150;
    }
  }

  &__loader {
    margin-left: @size-spacing-x300;
  }

  &__item {
    display: flex;
  }

  &__select {
    padding-left: @size-spacing-x300;
    white-space: nowrap;
  }
}
</style>
